import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Injector,
    Input,
    OnChanges,
    OnInit,
    SimpleChange,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessorValidatorBase } from '../control-value-accessor-validator-base';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { isDefined } from '../utils';

@Component({
    selector: 'fi-input-number-fraction',
    template: `
        <mat-form-field
            [appearance]="formFieldAppearance"
            [ngClass]="formFieldClassName"
        >
            <mat-label>{{ matLabel }}</mat-label>
            <input
                matInput
                #inputElement
                [required]="isRequired"
                fiNumberOnly
                [allowDecimals]="true"
                [allowLeadingZero]="false"
                [allowNegativeNumbers]="isNegativeNumbersAllowed"
                (focus)="inputElement.select()"
                [(ngModel)]="valueStr"
                (ngModelChange)="onValueStrChange()"
            />
            <span matSuffix>{{ matSuffixText }}</span>
        </mat-form-field>
    `,
    styleUrls: ['./fi-input-number-fraction.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-input-number-fraction' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiInputNumberFractionComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiInputNumberFractionComponent,
        },
    ],
    standalone: false,
})
export class FiInputNumberFractionComponent
    extends ControlValueAccessorValidatorBase<number>
    implements OnInit, OnChanges
{
    @Input() formFieldAppearance: MatFormFieldAppearance = 'outline';
    @Input() matLabel: string = '';
    @Input() matSuffixText: string = '';
    @Input() isRequired: boolean = true;
    @Input() minimumFractionDigits: number = 1;
    @Input() maximumFractionDigits: number = 2;
    @Input() formFieldClassName: string = '';
    @Input() isNegativeNumbersAllowed: boolean;
    @ViewChild('inputElement', { static: true }) inputElementRef: ElementRef<HTMLInputElement>;

    valueStr: string = '';

    constructor(private injector: Injector) {
        super(injector);
    }

    ngOnInit() {
        this.convertModelToFractionString();
    }

    ngOnChanges(changes: SimpleChanges) {
        const change: SimpleChange = changes.ngModel;
        if (change && !isDefined(change.currentValue)) this.convertModelToFractionString();
    }

    convertModelToFractionString() {
        if (!isDefined(this.ngModel) || Number.isNaN(this.ngModel)) {
            this.ngModel = 0;
            this.doPropagateChanges();
        }

        this.valueStr = this.ngModel.toLocaleString('en-US', {
            useGrouping: false,
            minimumFractionDigits: this.minimumFractionDigits,
            maximumFractionDigits: this.maximumFractionDigits,
        });
        this.inputElementRef.nativeElement.value = this.valueStr;
        this.cdr.markForCheck();
    }

    onValueStrChange() {
        this.valueStr = this.validateRangeValue(this.valueStr);
        this.ngModel = Number(this.valueStr);
        this.doPropagateChanges();
    }

    private validateRangeValue(value: string): string {
        const splitStr: string[] = value.split('.');

        if (splitStr.length < 3) return value;

        splitStr.pop();
        return splitStr.join('.');
    }
}
